import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { jwtDecode } from 'jwt-decode'
import { fetchWithAuth } from '../../utils/apiClient'
import AuthNavBar from '../Dashboard/AuthNavBar'
import SidePanel from '../SidePanel/SidePanel'
import useGlobalTheme from '../../hooks/useGlobalTheme'

function VerifyEmail() {
	const theme = useGlobalTheme()
	const isLight = theme === 'light'
	const [code, setCode] = useState('')
	const [isLoading, setIsLoading] = useState(false)
	const [error, setError] = useState('')
	const [success, setSuccess] = useState('')
	const [isPanelOpen, setIsPanelOpen] = useState(false)
	const [resendCooldown, setResendCooldown] = useState(120)
	const [isResending, setIsResending] = useState(false)
	const navigate = useNavigate()
	const location = useLocation()

	const jwtToken = localStorage.getItem('jwtToken')
	const user = jwtToken ? jwtDecode(jwtToken) : null
	const email = location.state?.email || user?.email || ''

	useEffect(() => {
		if (resendCooldown > 0) {
			const timer = setInterval(() => {
				setResendCooldown(prev => Math.max(0, prev - 1))
			}, 1000)
			return () => clearInterval(timer)
		}
	}, [resendCooldown])

	const handleSubmit = async (e) => {
		e.preventDefault()
		
		if (!email) {
			setError('Email address is required')
			return
		}
		
		if (!code || code.length !== 6) {
			setError('Please enter a 6-digit verification code')
			return
		}

		setIsLoading(true)
		setError('')

		try {
			const response = await fetchWithAuth('/api/auth/verify-email', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ code }),
			})

			const data = await response.json()

			if (response.ok) {
				setSuccess('Email verified successfully!')
				setTimeout(() => {
					navigate('/tutorial')
				}, 1500)
			} else {
				setError(data.message || 'Verification failed')
			}
		} catch (error) {
			console.error('Verification error:', error)
			setError('An error occurred. Please try again.')
		} finally {
			setIsLoading(false)
		}
	}

	const handleResendCode = async () => {
		if (resendCooldown > 0 || isResending) return

		setIsResending(true)
		setError('')
		setSuccess('')

		try {
			const response = await fetchWithAuth('/api/auth/resend-verification', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({}),
			})

			const data = await response.json()

			if (response.ok) {
				setSuccess('Verification code sent!')
				setError('')
				setResendCooldown(120)
			} else if (response.status === 429) {
				setError(data.message || 'Please wait before requesting another code.')
				if (data.remainingSeconds) {
					setResendCooldown(data.remainingSeconds)
				}
			} else {
				setError(data.message || 'Failed to resend code')
			}
		} catch (error) {
			setError('Failed to resend code. Please try again.')
		} finally {
			setIsResending(false)
		}
	}

	return (
		<div className={`min-h-screen ${isLight ? 'bg-white' : 'bg-[#15202b]'}`}>
			<AuthNavBar />
			<div className={`border-b ${isLight ? 'border-gray-200' : 'border-gray-700'}`}></div>
			<SidePanel
				isOpen={isPanelOpen}
				onOpen={() => setIsPanelOpen(true)}
				onClose={() => setIsPanelOpen(false)}
			/>

			<div className="flex flex-col justify-center py-12 sm:px-6 lg:px-8">
				<div className="sm:mx-auto sm:w-full sm:max-w-md">
					<h2 className={`mt-6 text-center text-3xl font-extrabold ${isLight ? 'text-gray-900' : 'text-white'}`}>
						Verify Your Email
					</h2>
					<p className={`mt-2 text-center text-sm ${isLight ? 'text-gray-500' : 'text-gray-400'}`}>
						We sent a 6-digit code to {email}
					</p>
				</div>

			<div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
				<div className={`py-8 px-4 shadow sm:rounded-lg sm:px-10 ${isLight ? 'bg-white ring-1 ring-gray-200' : 'bg-gray-800'}`}>
					<form onSubmit={handleSubmit} className="space-y-6">
						<div>
							<label htmlFor="code" className={`block text-sm font-medium ${isLight ? 'text-gray-700' : 'text-gray-300'}`}>
								Verification Code
							</label>
							<div className="mt-1">
								<input
									id="code"
									name="code"
									type="text"
									required
									maxLength="6"
									value={code}
									onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
									className={`appearance-none block w-full px-3 py-2 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm text-center font-mono text-2xl tracking-widest ${isLight ? 'border border-gray-300 placeholder-gray-400 bg-white text-gray-900' : 'border border-gray-600 placeholder-gray-500 bg-gray-700 text-white'}`}
									placeholder="000000"
								/>
							</div>
						</div>

						{error && (
							<div className={`text-sm text-center ${isLight ? 'text-red-600' : 'text-red-400'}`}>{error}</div>
						)}

						{success && (
							<div className={`text-sm text-center ${isLight ? 'text-green-600' : 'text-green-400'}`}>{success}</div>
						)}

						<div>
							<button
								type="submit"
								disabled={isLoading || !email || code.length !== 6}
								className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
							>
								{isLoading ? 'Verifying...' : 'Verify Email'}
							</button>
						</div>

						<div className="text-center">
							<button
								type="button"
								onClick={handleResendCode}
								disabled={resendCooldown > 0 || isResending}
								className="text-sm text-blue-400 hover:text-blue-300 disabled:text-gray-500 disabled:cursor-not-allowed"
							>
								{isResending
									? 'Sending...'
									: resendCooldown > 0
									? `Resend code in ${resendCooldown}s`
									: 'Resend verification code'}
							</button>
						</div>
					</form>
				</div>
			</div>
			</div>
		</div>
	)
}

export default VerifyEmail
